import { isAdminChunkLoadFailure } from '../../shared/errors/isAdminChunkLoadFailure.js';

import {

  ADMIN_PWA_REFRESH_ESCALATION_STEPS,

  runLiveAdminPwaRefreshEscalationStep,

  type AdminPwaRefreshEscalationStep,

} from './adminPwaRefreshEscalation.js';



export { isAdminChunkLoadFailure };



/** Session latch — blocks repeated *auto* recovery only; user Retry bypasses. */

export const ADMIN_CHUNK_RECOVERY_LATCH_KEY = 'admin_chunk_auto_recovery_attempt';



/** Watchdog between escalation steps when reload/replace does not unload the document. */

export const ADMIN_CHUNK_RECOVERY_WATCHDOG_MS = 3_000;



export type AdminChunkRecoverySource = 'auto' | 'user-retry' | 'boundary-auto';



let recoveryInFlight = false;

let escalationIndex = 0;

let watchdogId: number | null = null;

const inFlightListeners = new Set<(inFlight: boolean) => void>();



function notifyInFlight(inFlight: boolean): void {

  recoveryInFlight = inFlight;

  for (const listener of inFlightListeners) {

    listener(inFlight);

  }

}



function clearWatchdog(): void {

  if (watchdogId != null) {

    window.clearTimeout(watchdogId);

    watchdogId = null;

  }

}



export function isAdminChunkAutoRecoveryLatchSet(): boolean {

  try {

    return sessionStorage.getItem(ADMIN_CHUNK_RECOVERY_LATCH_KEY) === '1';

  } catch {

    return false;

  }

}



function setAutoRecoveryLatch(): void {

  try {

    sessionStorage.setItem(ADMIN_CHUNK_RECOVERY_LATCH_KEY, '1');

  } catch {

    // ignore

  }

}



/** Clear auto-recovery latch after lazy shell + route chunks succeed. */

export function clearAdminChunkLoadRecoveryLatch(): void {

  try {

    sessionStorage.removeItem(ADMIN_CHUNK_RECOVERY_LATCH_KEY);

  } catch {

    // ignore

  }

}



function isAdminChunkRecoveryActive(): boolean {
  return recoveryInFlight;
}



/**

 * Called when a lazy-loaded shell (DevShellLayout, Dashboard, …) mounts —

 * chunk for that shell succeeded. No-op while recovery is active (G1).

 */

export function markAdminChunkShellReady(): void {

  if (isAdminChunkRecoveryActive()) {

    return;

  }

  clearAdminChunkLoadRecoveryLatch();

  clearWatchdog();

  escalationIndex = 0;

  notifyInFlight(false);

}



/** Alias for lazy shell mount callbacks. */

export function signalAdminLazyShellMounted(): void {

  markAdminChunkShellReady();

}



export function isAdminChunkRecoveryInFlight(): boolean {

  return recoveryInFlight;

}



export function subscribeAdminChunkRecoveryInFlight(

  listener: (inFlight: boolean) => void,

): () => void {

  inFlightListeners.add(listener);

  listener(recoveryInFlight);

  return () => {

    inFlightListeners.delete(listener);

  };

}



function scheduleEscalationWatchdog(source: AdminChunkRecoverySource): void {

  clearWatchdog();

  watchdogId = window.setTimeout(() => {

    watchdogId = null;

    const maxIndex = ADMIN_PWA_REFRESH_ESCALATION_STEPS.length - 1;

    if (escalationIndex < maxIndex) {

      escalationIndex += 1;

      void executeRecoveryStep(source);

      return;

    }

    notifyInFlight(false);

    // Terminal exhaustion — release auto latch so ErrorBoundary Retry is reachable (G1).
    clearAdminChunkLoadRecoveryLatch();
  }, ADMIN_CHUNK_RECOVERY_WATCHDOG_MS);

}



async function executeRecoveryStep(source: AdminChunkRecoverySource): Promise<void> {

  const step: AdminPwaRefreshEscalationStep =

    ADMIN_PWA_REFRESH_ESCALATION_STEPS[escalationIndex] ?? 'origin_replace';

  try {

    await runLiveAdminPwaRefreshEscalationStep(step);

    scheduleEscalationWatchdog(source);

  } catch {

    window.location.reload();

  }

}



export interface AdminChunkLoadRecoveryRequest {

  readonly source: AdminChunkRecoverySource;

  /** User Retry — bypass auto latch and restart ladder from step 0. */

  readonly force?: boolean;

}



/**

 * Shared recovery entry for preload errors, window error/rejection, ErrorBoundary,

 * and Retry on chunk failures.

 */

export async function requestAdminChunkLoadRecovery(

  request: AdminChunkLoadRecoveryRequest,

): Promise<void> {

  const isUserRetry = request.source === 'user-retry';



  if (!isUserRetry && !request.force) {

    if (recoveryInFlight || isAdminChunkAutoRecoveryLatchSet()) {

      return;

    }

    setAutoRecoveryLatch();

    escalationIndex = 0;

    notifyInFlight(true);

  } else {

    clearAdminChunkLoadRecoveryLatch();

    clearWatchdog();

    escalationIndex = 0;

    notifyInFlight(true);

  }



  await executeRecoveryStep(request.source);

}



/** Register global listeners (main boot). */

export function registerAdminChunkLoadRecovery(): void {

  if (typeof window === 'undefined') {

    return;

  }



  window.addEventListener('vite:preloadError', (event) => {

    event.preventDefault();

    void requestAdminChunkLoadRecovery({ source: 'auto' });

  });



  window.addEventListener('unhandledrejection', (event) => {

    if (!isAdminChunkLoadFailure(event.reason)) {

      return;

    }

    event.preventDefault();

    void requestAdminChunkLoadRecovery({ source: 'auto' });

  });



  window.addEventListener(

    'error',

    (event) => {

      const candidate = event.error ?? event.message;

      if (!isAdminChunkLoadFailure(candidate)) {

        return;

      }

      event.preventDefault();

      void requestAdminChunkLoadRecovery({ source: 'auto' });

    },

    true,

  );



  window.addEventListener('pagehide', () => {

    clearWatchdog();

  });

}



/** Test-only introspection — escalation ladder step index. */

export function getAdminChunkRecoveryEscalationIndexForTests(): number {

  return escalationIndex;

}



/** Test-only introspection — watchdog scheduled. */

export function isAdminChunkRecoveryWatchdogScheduledForTests(): boolean {

  return watchdogId != null;

}



/** Test-only reset between cases. */

export function resetAdminChunkRecoveryStateForTests(): void {

  clearWatchdog();

  escalationIndex = 0;

  clearAdminChunkLoadRecoveryLatch();

  notifyInFlight(false);

}


